/* iter-51t — Admin UI for installing Canva Connect API credentials.
 *
 * Keys are stored in `app_config` (Mongo) encrypted-at-rest via the same
 * Fernet cipher used for Canva refresh tokens. Environment-variable
 * fallback is preserved — the DB-stored value wins when present.
 *
 * Secret is never returned in plaintext or in partial-preview form.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Palette, Copy, PlugZap, CheckCircle2, XCircle, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { timeAgo } from '@/lib/format';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function CanvaCredsConfig() {
  const [creds, setCreds] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [alertEmail, setAlertEmail] = useState('');

  const reload = async () => {
    try {
      const r = await axios.get(`${API}/admin/canva/creds`);
      setCreds(r.data);
      setAlertEmail(r.data?.alert_email || '');
    } catch {
      toast.error('Could not load Canva credentials.');
    }
  };

  useEffect(() => { reload(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      if (clientId.trim())     body.canva_client_id     = clientId.trim();
      if (clientSecret.trim()) body.canva_client_secret = clientSecret.trim();
      // alert_email is always sent (so admins can clear it by submitting "")
      // as long as it has changed from the loaded value.
      if (alertEmail.trim() !== (creds?.alert_email || '')) {
        body.canva_alert_email = alertEmail.trim();
      }
      if (!Object.keys(body).length) {
        toast.error('Enter at least one value to save.');
        return;
      }
      await axios.put(`${API}/admin/canva/creds`, body);
      toast.success('Canva credentials saved.');
      setClientSecret('');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear the stored Canva credentials? Authors will be unable to design covers until you re-install them.')) return;
    setBusy(true);
    try {
      await axios.put(`${API}/admin/canva/creds`, {
        canva_client_id: '', canva_client_secret: '',
      });
      toast.success('Canva credentials cleared.');
      setClientId(''); setClientSecret('');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed.');
    } finally {
      setBusy(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/admin/canva/test-connection`);
      setTestResult(r.data);
      if (r.data?.ok) {
        toast.success(r.data.message || 'Canva connection looks good.');
      } else {
        toast.error(r.data?.message || 'Canva connection test failed.');
      }
      // Refresh creds so the "Last tested ..." stamp picks up the persisted timestamp.
      await reload();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Test failed — see backend logs.';
      setTestResult({ ok: false, stage: 'request', status: null, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const copyRedirect = async () => {
    try {
      await navigator.clipboard.writeText(creds?.redirect_uri || '');
      toast.success('Redirect URI copied.');
    } catch {
      toast.error('Clipboard blocked — copy it manually.');
    }
  };

  const configured = !!(creds?.client_id && creds?.client_secret_set);

  return (
    <div className="mt-4 rounded-md border border-fuchsia-300/60 bg-fuchsia-50/60 p-4 space-y-3" data-testid="admin-canva-creds-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[12.5px] font-semibold flex items-center gap-1.5">
            <Palette className="h-3.5 w-3.5 text-fuchsia-700" /> Canva Connect API
          </div>
          <div className="text-[11px] text-muted-foreground">
            {configured
              ? <>Currently installed — ID <code className="font-mono-stat">{creds.client_id}</code> · secret configured</>
              : 'Awaiting Canva enterprise-tier approval. Authors see a "Canva covers on the way" card with Nano-Banana + manual-upload alternatives until you paste keys below.'}
            {(creds?.env_client_id || creds?.env_client_secret) && !configured && (
              <span className="ml-1 text-amber-700">(legacy env values detected; DB values will override once saved)</span>
            )}
          </div>
        </div>
      </div>

      {/* iter-53j — Interim banner while Canva enterprise approval is pending. */}
      {!configured && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-950">
          <div className="font-semibold">Interim cover-design workflow (until Canva keys arrive)</div>
          <ul className="ml-4 mt-1 list-disc space-y-0.5">
            <li>
              <strong>Bookstore admin → Books → Edit → Cover art</strong> —
              upload a designer-supplied PNG/JPG; auto-thumbnail + GridFS storage already
              ship.
            </li>
            <li>
              <strong>Generate book covers</strong> button on the Bookstore
              Admin Overview — fires the Nano-Banana batch generator
              (Gemini image gen, ~5 s/cover, charged to the Emergent LLM key).
            </li>
            <li>
              Once Canva sales approves, paste the keys here and
              <em> Test connection </em>— the &ldquo;Canva covers on the way&rdquo;
              card on the storefront flips to the full Canva flow within
              one minute (config lives in MongoDB, no redeploy needed).
            </li>
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label className="text-[11px]">Client ID</Label>
          <Input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder={creds?.client_id || 'OC-AbCdEf123…'}
            className="h-8 font-mono-stat text-[11.5px]"
            data-testid="admin-canva-client-id-input"
          />
        </div>
        <div>
          <Label className="text-[11px]">Client Secret</Label>
          <Input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={creds?.client_secret_set ? '••••••• (replace current)' : 'cnvks_…'}
            className="h-8 font-mono-stat text-[11.5px]"
            data-testid="admin-canva-client-secret-input"
          />
        </div>
      </div>

      <div>
        <Label className="text-[11px]">
          Alert email (optional)
          <span className="ml-1 text-muted-foreground font-normal">
            — get an email when a probe flips from <span className="text-emerald-700">OK</span> to <span className="text-rose-700">failing</span>
          </span>
        </Label>
        <Input
          type="email"
          value={alertEmail}
          onChange={(e) => setAlertEmail(e.target.value)}
          placeholder="ops@yourcompany.com"
          className="h-8 text-[11.5px]"
          data-testid="admin-canva-alert-email-input"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={save} disabled={busy} className="h-8 gap-1.5 bg-fuchsia-700 hover:bg-fuchsia-800" data-testid="admin-canva-creds-save">
          <Save className="h-3.5 w-3.5" /> Save
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={testConnection}
          disabled={testing || busy || !configured}
          className="h-8 gap-1.5 border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-100"
          data-testid="admin-canva-creds-test"
          title={configured ? 'Send a probe to Canva to verify the credentials' : 'Save credentials first'}
        >
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlugZap className="h-3.5 w-3.5" />}
          Test connection
        </Button>
        {configured && creds?.last_test_at && (
          <span
            data-testid="admin-canva-last-test-stamp"
            className={
              'inline-flex items-center gap-1 text-[10.5px] ' +
              (creds.last_test_ok === true
                ? 'text-emerald-700'
                : creds.last_test_ok === false
                  ? 'text-rose-700'
                  : 'text-muted-foreground')
            }
            title={creds.last_test_message || ''}
          >
            <Clock className="h-3 w-3" />
            Last tested {timeAgo(creds.last_test_at)}
            {creds.last_test_ok === true && ' ✓'}
            {creds.last_test_ok === false && ' ✗'}
          </span>
        )}
        {configured && (
          <Button size="sm" variant="ghost" onClick={clearAll} disabled={busy} className="h-8 gap-1.5 text-muted-foreground" data-testid="admin-canva-creds-clear">
            Clear
          </Button>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-1.5 text-[10.5px] text-muted-foreground">
          Register this redirect URI at the Canva Developer Portal:
          <code className="rounded bg-fuchsia-100 px-1.5 py-0.5 font-mono-stat" data-testid="admin-canva-redirect-uri">
            {creds?.redirect_uri || ''}
          </code>
          <button type="button" onClick={copyRedirect}
                  data-testid="admin-canva-copy-redirect"
                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-white px-1.5 py-0.5 text-[10px] hover:bg-secondary">
            <Copy className="h-2.5 w-2.5" /> Copy
          </button>
        </div>
      </div>

      {testResult && (
        <div
          data-testid="admin-canva-test-result"
          className={
            'flex items-start gap-2 rounded-md border px-3 py-2 text-[11px] ' +
            (testResult.ok
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
              : 'border-rose-300 bg-rose-50 text-rose-900')
          }
        >
          {testResult.ok
            ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
          <div className="space-y-0.5">
            <div className="font-medium">{testResult.message}</div>
            <div className="text-[10px] opacity-75">
              stage: <code className="font-mono-stat">{testResult.stage || '—'}</code>
              {testResult.status != null && (
                <> · HTTP <code className="font-mono-stat">{testResult.status}</code></>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="text-[10.5px] text-muted-foreground">
        Required Canva scopes: <code className="font-mono-stat">design:content:read</code> &middot;{' '}
        <code className="font-mono-stat">design:content:write</code>. Get keys at{' '}
        <a href="https://www.canva.com/developers/" target="_blank" rel="noopener noreferrer"
            className="text-fuchsia-800 underline hover:no-underline">canva.com/developers</a> →
        &ldquo;Create an integration&rdquo; → Connect APIs.
      </div>
    </div>
  );
}
